import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { presentationService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const Service = () => {
    const { slug } = useParams();
    const { language } = useLanguage();

    // API state
    const [service, setService] = useState(null);
    const [videos, setVideos] = useState([]);
    const [galleries, setGalleries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Gallery Detail Modal
    const [selectedGallery, setSelectedGallery] = useState(null);
    const [galleryDetail, setGalleryDetail] = useState(null);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);

    useEffect(() => {
        if (slug) {
            fetchServiceData();
            fetchVideos(0);
            fetchGalleries(0);
        } else {
            setError(language === 'fa' ? 'سرویس مورد نظر یافت نشد' : 'Service not found');
            setLoading(false);
        }
    }, [slug, language]);

    const fetchServiceData = async () => {
        try {
            const data = await presentationService.getServiceBySlug(slug);
            setService(data);
        } catch (err) {
            console.error('Service API error:', err);
            setError(language === 'fa' ? 'خطا در دریافت اطلاعات' : 'Error fetching service');
        } finally {
            setLoading(false);
        }
    };

    const fetchVideos = async (offset) => {
        try {
            const data = await presentationService.getServiceVideos(slug, 20, offset);
            setVideos(data.results || []);
        } catch (err) {
            console.error('Videos API error:', err);
        }
    };

    const fetchGalleries = async (offset) => {
        try {
            const data = await presentationService.getServiceGalleries(slug, 20, offset);
            setGalleries(data.results || []);
        } catch (err) {
            console.error('Galleries API error:', err);
        }
    };

    const openGalleryDetail = async (gallerySlug) => {
        setSelectedGallery(gallerySlug);
        setGalleryLoading(true);
        setActiveMediaIndex(0);
        try {
            const data = await presentationService.getGalleryBySlug(slug, gallerySlug);
            setGalleryDetail(data);
        } catch (err) {
            console.error('Gallery detail error:', err);
        } finally {
            setGalleryLoading(false);
        }
    };

    const closeGalleryDetail = () => {
        setSelectedGallery(null);
        setGalleryDetail(null);
        setActiveMediaIndex(0);
    };

    const goToPrevMedia = () => {
        if (!galleryDetail?.media?.length) return;
        setActiveMediaIndex((prev) => (prev === 0 ? galleryDetail.media.length - 1 : prev - 1));
    };

    const goToNextMedia = () => {
        if (!galleryDetail?.media?.length) return;
        setActiveMediaIndex((prev) => (prev === galleryDetail.media.length - 1 ? 0 : prev + 1));
    };

    // API verilerini UI formatına dönüştür
    const antecedenceVideos = videos.filter(v => v.antecedence === true);
    const normalVideos = videos.filter(v => v.antecedence !== true);

    // Loading
    if (loading) {
        return (
            <div className="flex justify-center items-center py-20 font-iransansRegular min-h-screen">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[#292DD8] font-bold text-lg">{language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</p>
                </div>
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div className="flex justify-center items-center py-20 font-iransansRegular min-h-screen">
                <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-6">
                    <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                    <p className="text-red-600 font-bold text-lg">{error}</p>
                    <button onClick={fetchServiceData} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">{language === 'fa' ? 'تلاش مجدد' : 'Try Again'}</button>
                </div>
            </div>
        );
    }

    if (!service) {
        return (
            <div className="flex justify-center items-center py-20 font-iransansRegular min-h-screen">
                <p className="text-gray-500 text-lg">{language === 'fa' ? 'سرویس مورد نظر یافت نشد' : 'Service not found'}</p>
            </div>
        );
    }

    const VideoFrame = ({ video, width = 474, height = 280, showIcon = true }) => (
        <div
            className="relative rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ backgroundColor: '#D3D4FF', width: '100%', maxWidth: width, aspectRatio: `${width}/${height}` }}
        >
            {video ? (
                <video src={video} controls className="w-full h-full object-cover" />
            ) : showIcon ? (
                <motion.div
                    className="w-8 h-8 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full bg-white/80 flex items-center justify-center cursor-pointer shadow-lg"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-[#292DD8] mr-0.5 lg:mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                </motion.div>
            ) : null}
        </div>
    );

    const SmallVideoFrame = ({ video, title, description }) => (
        <motion.div className="flex flex-col gap-1.5 sm:gap-2" whileHover={{ scale: 1.02 }}>
            <div
                className="relative rounded-lg sm:rounded-xl flex items-center justify-center cursor-pointer overflow-hidden"
                style={{ backgroundColor: '#D3D4FF', width: '100%', aspectRatio: '16/10', minHeight: '120px' }}
            >
                {video ? (
                    <video src={video} controls className="w-full h-full object-cover" />
                ) : (
                    <motion.div className="w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-white/80 flex items-center justify-center shadow-md" whileHover={{ scale: 1.1 }}>
                        <svg className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-[#292DD8] mr-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </motion.div>
                )}
            </div>
            <h4 className="text-[#292DD8] font-extrabold text-[11px] sm:text-sm lg:text-base text-right">{title}</h4>
            <p className="text-[#000377] text-[10px] sm:text-xs lg:text-sm text-right leading-relaxed">{description}</p>
        </motion.div>
    );

    return (
        <div className="font-iransansRegular overflow-hidden" dir="rtl">
            {/* ========== BANNER ========== */}
            <div className="relative w-full overflow-hidden min-h-[320px] sm:min-h-0">
                <img
                    src={service?.header?.background || "/upd.png"}
                    alt="Banner"
                    className="hidden md:block w-full h-full object-cover absolute inset-0"
                />
                <img
                    src={service?.header?.background || "/upm.png"}
                    alt="Banner"
                    className="block md:hidden w-full h-full object-cover absolute inset-0"
                />
                <img src={service?.header?.background || "/upd.png"} alt="" className="hidden md:block w-full h-auto invisible" />
                <div className="absolute inset-0 " />

                <div className="absolute inset-0">
                    <div className="max-w-[1136px] mx-auto px-4 sm:px-6 h-full flex items-center">
                        <div className="flex flex-row items-center justify-between w-full h-full gap-2 sm:gap-8">
                            {service?.header?.foreground && (
                                <motion.img
                                    src={service.header.foreground}
                                    alt="Service"
                                    className="w-[157px] md:w-[363px] h-auto object-contain flex-shrink-0 self-end mb-4 sm:mb-0"
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                />
                            )}
                            <motion.div
                                className="flex-1 min-w-0 text-right z-10 py-6 sm:py-0"
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h1 className="text-xl sm:text-4xl lg:text-[96px] md:w-[943px] w-[206px] font-extrabold text-white mb-3 sm:mb-4 lg:mb-8 leading-relaxed lg:leading-[1.5]">
                                    {service?.title || (language === 'fa' ? 'تولید محتوا' : 'Content Production')}
                                </h1>
                                <p className="text-white/90 font-iransansBold text-[11px] sm:text-base lg:text-[32px] leading-relaxed lg:leading-[1.5] md:w-[743px] w-[206px] ">
                                    {service?.description || (language === 'fa'
                                        ? 'در طول بیش از یک دهه فعالیت، کاریز اعتماد مسئولیت برگزاری بیش از 300 رویداد، همایش و نشست تخصصی، و اجرای بیش از 60 پروژه'
                                        : 'Over a decade of activity, Kariz Etemad has been responsible for organizing more than 300 events.'
                                    )}
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== İÇERİK ========== */}
            <div className="max-w-[1136px] mx-auto px-3 sm:px-6 py-6 sm:py-12 lg:py-20">
                <motion.h2
                    className="text-lg sm:text-3xl lg:text-5xl font-extrabold text-[#292DD8] mb-2 sm:mb-4 lg:mb-6 text-right"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    {service?.title || (language === 'fa' ? 'تولید محتوا' : 'Content Production')}
                </motion.h2>

                <motion.p
                    className="text-[#000377] text-[11px] sm:text-base lg:text-lg leading-relaxed mb-6 sm:mb-10 lg:mb-12 text-right max-w-3xl"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    {service?.description || (language === 'fa'
                        ? 'در طول بیش از یک دهه فعالیت، کاریز اعتماد مسئولیت برگزاری بیش از 300 رویداد، همایش و نشست تخصصی، و اجرای بیش از 60 پروژه غرفه‌سازی در نمایشگاه‌های داخلی و بین‌المللی را بر عهده داشته است.'
                        : 'Over a decade of activity, Kariz Etemad has been responsible for organizing more than 300 events.'
                    )}
                </motion.p>

                {/* Antecedence Videolar - bir sağda bir solda */}
                {antecedenceVideos.map((video, index) => (
                    <div key={video.slug} className={`flex flex-row items-center gap-3 sm:gap-6 lg:gap-10 mb-6 sm:mb-10 lg:mb-12`}>
                        {index % 2 === 0 ? (
                            <>
                                <motion.div className="w-[45%] lg:w-[474px] flex-shrink-0" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                    <VideoFrame video={video.video} width={474} height={280} />
                                </motion.div>
                                <motion.div className="flex-1 space-y-1.5 sm:space-y-3 lg:space-y-4 text-right min-w-0" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                    <h3 className="text-[#292DD8] font-bold text-sm sm:text-lg">{video.title}</h3>
                                    <p className="text-[#000377] text-[10px] sm:text-sm lg:text-base leading-relaxed">{video.description}</p>
                                </motion.div>
                            </>
                        ) : (
                            <>
                                <motion.div className="flex-1 space-y-1.5 sm:space-y-3 lg:space-y-4 text-right min-w-0" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                    <h3 className="text-[#292DD8] font-bold text-sm sm:text-lg">{video.title}</h3>
                                    <p className="text-[#000377] text-[10px] sm:text-sm lg:text-base leading-relaxed">{video.description}</p>
                                </motion.div>
                                <motion.div className="w-[45%] lg:w-[474px] flex-shrink-0" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                    <VideoFrame video={video.video} width={474} height={280} />
                                </motion.div>
                            </>
                        )}
                    </div>
                ))}

                {/* Normal Videolar Grid */}
                {normalVideos.length > 0 && (
                    <div className="mt-10 sm:mt-16 lg:mt-24">
                        <motion.h2 className="text-base sm:text-2xl lg:text-4xl font-extrabold text-[#292DD8] mb-4 sm:mb-8 lg:mb-12 text-right" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            {language === 'fa' ? 'ویدیوها' : 'Videos'}
                        </motion.h2>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
                            {normalVideos.map((video) => (
                                <motion.div key={video.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                                    <SmallVideoFrame video={video.video} title={video.title} description={video.description} />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Galeriler */}
                {galleries.length > 0 && (
                    <div className="mt-10 sm:mt-16 lg:mt-24">
                        <motion.h2 className="text-base sm:text-2xl lg:text-4xl font-extrabold text-[#292DD8] mb-4 sm:mb-8 lg:mb-12 text-right" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            {language === 'fa' ? 'گالری' : 'Gallery'}
                        </motion.h2>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
                            {galleries.map((gallery) => (
                                <motion.div
                                    key={gallery.slug}
                                    className="bg-[#EFEFFF] rounded-lg sm:rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all"
                                    whileHover={{ scale: 1.02 }}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    onClick={() => openGalleryDetail(gallery.slug)}
                                >
                                    <div
                                        className="relative w-full mx-auto overflow-hidden"
                                        style={{ backgroundColor: '#D3D4FF', maxWidth: 349, aspectRatio: '349/244' }}
                                    >
                                        {gallery.banner && (
                                            <img src={gallery.banner} alt={gallery.title} className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="p-3 sm:p-4 lg:p-5">
                                        <p className="text-xs text-gray-500 mb-1 sm:mb-2">{gallery.date}</p>
                                        <h4 className="text-[#292DD8] font-extrabold text-sm sm:text-lg text-right mb-1">{gallery.title}</h4>
                                        {gallery.description && (
                                            <p className="text-[#000377] text-[10px] sm:text-xs text-right leading-relaxed line-clamp-2">{gallery.description}</p>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ========== GALERİ DETAY MODAL ========== */}
            <AnimatePresence>
                {selectedGallery && (
                    <motion.div
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeGalleryDetail}
                    >
                        <motion.div
                            className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto relative"
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.25 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button
                                onClick={closeGalleryDetail}
                                className="absolute top-0 right-1  sm:right-2 z-20 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors"
                                aria-label="close"
                            >
                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            {galleryLoading ? (
                                <div className="flex justify-center py-24">
                                    <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : galleryDetail ? (
                                <div dir="rtl" className="p-4 sm:p-6 lg:p-8">
                                    {/* Top section: big image + info */}
                                    <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-10">
                                        {/* Sol: büyük görsel + ok navigasyonu */}
                                        <div className="relative w-full lg:w-[55%] flex-shrink-0">
                                            <div
                                                className="relative rounded-xl sm:rounded-2xl overflow-hidden bg-[#D3D4FF]"
                                                style={{ aspectRatio: '375/280' }}
                                            >
                                                {galleryDetail.media?.length > 0 ? (
                                                    <img
                                                        src={galleryDetail.media[activeMediaIndex]?.image}
                                                        alt={galleryDetail.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[#292DD8]/50 text-sm">
                                                        {language === 'fa' ? 'بدون تصویر' : 'No image'}
                                                    </div>
                                                )}

                                                {galleryDetail.media?.length > 1 && (
                                                    <>
                                                        <button
                                                            onClick={goToPrevMedia}
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors"
                                                            aria-label="previous"
                                                        >
                                                            <svg className="w-4 h-4 text-[#292DD8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={goToNextMedia}
                                                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors"
                                                            aria-label="next"
                                                        >
                                                            <svg className="w-4 h-4 text-[#292DD8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5l-7 7 7 7" />
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Sağ: başlık + meta bilgiler */}
                                        <div className="flex-1 min-w-0 flex flex-col">
                                            <div className='flex items-center gap-1'>
                                                <img src="/modal-logo.png" alt="" className='w-[17px] h-[46px] -mt-4' />
                                                <h3 className="text-base sm:text-xl lg:text-2xl font-extrabold text-[#292DD8] mb-4 sm:mb-6 leading-relaxed text-center">
                                                    {galleryDetail.title}
                                                </h3>
                                            </div>

                                            <div className="space-y-3 sm:space-y-4 text-[11px] sm:text-sm">
                                                {galleryDetail.venue && (
                                                    <div className="flex justify-between border-b border-gray-100 pb-2 sm:pb-3">
                                                        <span className="text-gray-400 font-bold">{language === 'fa' ? 'مکان برگزاری:' : 'Venue:'}</span>
                                                        <span className="text-[#000377]">{galleryDetail.venue}</span>
                                                    </div>
                                                )}
                                                {galleryDetail.date && (
                                                    <div className="flex justify-between border-b border-gray-100 pb-2 sm:pb-3">
                                                        <span className="text-gray-400 font-bold">{language === 'fa' ? 'زمان:' : 'Date:'}</span>
                                                        <span className="text-[#000377]">{galleryDetail.date}</span>
                                                    </div>
                                                )}
                                                {galleryDetail.specifications && (
                                                    <div className="flex justify-between border-b border-gray-100 pb-2 sm:pb-3">
                                                        <span className="text-gray-400 font-bold">{language === 'fa' ? 'مشخصات غرفه:' : 'Specs:'}</span>
                                                        <span className="text-[#000377]">{galleryDetail.specifications}</span>
                                                    </div>
                                                )}
                                                {galleryDetail.services?.length > 0 && (
                                                    <div className="flex justify-between gap-2">
                                                        <span className="text-gray-400 font-bold whitespace-nowrap">{language === 'fa' ? 'خدمات:' : 'Services:'}</span>
                                                        <span className="text-[#000377] text-right">
                                                            {galleryDetail.services.map((s) => s.title).join(' | ')}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Tüm görseller: tek satır, yatay scroll */}
                                            {galleryDetail.media?.length > 1 && (
                                                <div className="mt-6 sm:mt-8">
                                                    <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-[#D3D4FF] [&::-webkit-scrollbar-thumb]:rounded-full">
                                                        {galleryDetail.media.map((item, i) => (
                                                            <button
                                                                key={i}
                                                                onClick={() => setActiveMediaIndex(i)}
                                                                className={`relative flex-shrink-0 w-20 h-14 sm:w-24 sm:h-16 rounded-md sm:rounded-lg overflow-hidden transition-all ${i === activeMediaIndex
                                                                        ? 'ring-2 ring-[#292DD8] ring-offset-1'
                                                                        : 'opacity-80 hover:opacity-100'
                                                                    }`}
                                                            >
                                                                <img src={item.image} alt="" className="w-full h-full object-cover" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Service;